import { useIsAuthenticated } from '@azure/msal-react';
import { createContext, Dispatch, ReactElement, useContext, useEffect, useReducer } from 'react';
import { localAuth } from '../../api/auth';
import { AppConfigDto } from '../../models/AppConfigModel';
import { ReducerAction } from './actions';
import { setAllConfiguration } from './config-fetcher';
import { reducer } from './reducer';
import { useLocation } from 'react-router-dom';
import excludedPages from './excluded-pages';

export type ConfigurationState = {
    headerOffsetHeight: number;
    resDragModalZIndex: number;
    appConfigs: AppConfigDto[];
};

export const configurationInitialState: ConfigurationState = {
    headerOffsetHeight: 0,
    resDragModalZIndex: 100,
    appConfigs: [] as AppConfigDto[]
};

const ConfigurationContext = createContext<ConfigurationState>(configurationInitialState);
const ConfigurationDispatchContext = createContext<Dispatch<ReducerAction>>(() => ({}));

function useConfigurationState() {
    const context = useContext(ConfigurationContext);
    if (!context) throw new Error('useConfigurationState must be used in ConfigurationProvider');
    return context;
}

function useAppConfigState() {
    return useConfigurationState().appConfigs;
}

function useFindAppConfig(configName: string) {
    return useAppConfigState().find((ac) => ac.name === configName);
}

function useFilterAppConfigs(configName: string) {
    return useAppConfigState().filter((ac) => ac.name === configName);
}

function useConfigurationDispatch() {
    const context = useContext(ConfigurationDispatchContext);
    if (!context) throw new Error('useConfigurationDispatch must be used in ConfigurationProvider');

    return context;
}

export type ConfigurationProviderProps = {
    children: React.ReactNode | ReactElement | ReactElement[];
};

const ConfigurationProvider = ({ children }: ConfigurationProviderProps) => {
    const [state, dispatch] = useReducer(reducer, configurationInitialState);

    const loc = useLocation();

    const isAuth = useIsAuthenticated();
    const isLocalAuth = localAuth.isAuth();
    useEffect(() => {
        const isPageExcluded = excludedPages.some(
            (p) =>
                p ===
                (loc.pathname.endsWith('/')
                    ? loc.pathname.slice(0, loc.pathname.length - 1)
                    : loc.pathname)
        );

        !isPageExcluded && isAuth && isLocalAuth && setAllConfiguration(dispatch);
    }, [isAuth, isLocalAuth, dispatch, loc]);

    return (
        <ConfigurationContext.Provider value={state as ConfigurationState}>
            <ConfigurationDispatchContext.Provider value={dispatch}>
                {children}
            </ConfigurationDispatchContext.Provider>
        </ConfigurationContext.Provider>
    );
};

export {
    useConfigurationState,
    useConfigurationDispatch,
    useAppConfigState,
    useFindAppConfig,
    useFilterAppConfigs
};

export default ConfigurationProvider;
